import React from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { XCircle, Brain } from 'lucide-react';
import AIFrameworkPreview from '../AIFrameworkPreview';

interface AiPreviewPhaseProps {
  aiDraft: any;
  onBack: () => void;
}

export const AiPreviewPhase: React.FC<AiPreviewPhaseProps> = ({ aiDraft, onBack }) => {
  if (!aiDraft) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-2 border-red-200 shadow-xl">
          <CardContent className="py-12">
            <XCircle className="w-16 h-16 text-red-500 mb-4" />
            <h3 className="text-xl font-semibold text-red-900 mb-2">No Framework Generated</h3>
            <p className="text-gray-600 mb-6">Unable to generate framework from description.</p>
            <Button onClick={onBack} variant="outline">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button onClick={onBack} variant="outline" className="mb-4">
          ← Back to Upload
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Brain className="w-8 h-8 text-purple-600" />
          AI Generated Framework Preview
        </h1>
        <p className="text-gray-600 mt-2">
          Review and edit the AI-generated compliance framework before finalizing.
        </p>
      </div>

      {/* AI Accuracy Disclaimer */}
      <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 mt-0.5 flex-shrink-0"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
          <div>
            <p className="text-sm font-bold text-amber-900 text-left">Important: AI-Generated Content — Review Required</p>
            <p className="text-xs text-amber-800 mt-1 text-left">
              This framework was generated by AI and is an <strong>approximation</strong>. It may contain inaccuracies,
              missing clauses, or incorrect requirement numbering. A qualified compliance professional should review
              and validate this structure before it is used in production. For the most accurate results, upload the
              official standard document (PDF) through the Document Parser instead.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <AIFrameworkPreview initialDraft={aiDraft} />
      </div>
    </div>
  );
};
